import { Box } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { FINANCING_PATIENTS_QUERY } from '../../graphql';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { QUEUE_PAGE_ROWS, useQueuePage } from '../../hooks/useQueuePage';
import { readyToScheduleSubscriptionParams } from '../../services/readyToScheduleScope';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  isQueueFiltered,
  LEAD_STATUS_FILTER_OPTIONS,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterValues,
  TRACK_FILTER_OPTIONS,
} from '../../services/queueFilters';
import { buildToScheduleFilter, matchesToSchedule } from '../../services/toScheduleFilters';
import { buildToScheduleRows, ToScheduleRow } from '../../services/toScheduleRows';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { lastContactColumn, patientColumn, paymentTrackColumn, pnColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { statusColumn } from './statusColumn';
import { preferredDateColumn, readySinceColumn } from './toScheduleColumns';
import { EditedRow, useHubRowEdits } from './useHubRowEdits';

/**
 * The Intake hub's To schedule tab (BAR-3096): every track's Ready to schedule patients, the PN's
 * list of dates to book, longest waiting first. Still the Financing & approval stage; booking it
 * is Won. Membership and the filters run on the server on the stage and payment status tags.
 */

const DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, status: 'active' };

type HubToScheduleRow = EditedRow<ToScheduleRow>;

const HYDRATION_NEEDS = {
  consult: true,
  procedure: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface ToScheduleTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function ToScheduleTab({ now }: ToScheduleTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);
  const { canViewPayments } = useCurrentUser().currentUserPermissions;

  const { values, setValues, setFilter, defaults, pnOptions } = useHubFilterValues(DEFAULTS);
  const filter = useMemo(() => buildToScheduleFilter(values), [values]);
  const subscriptionParams = useMemo(() => readyToScheduleSubscriptionParams(), []);

  const {
    rows: data,
    total,
    loading,
    error,
    page,
    setPage,
  } = useQueuePage<Patient>(FINANCING_PATIENTS_QUERY, 'Patient', filter, subscriptionParams);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the to-schedule queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const patients = useMemo(() => (error ? [] : data), [data, error]);
  const patientRefs = useMemo(
    () =>
      patients
        .map((patient) => (patient.id ? getReferenceString(patient) : undefined))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: lastCommunicationFloor(today) },
    `intake-to-schedule:${filter}`
  );

  const builtRows = useMemo(
    () => buildToScheduleRows(patients, hydration, resolvedNow, canViewPayments),
    [patients, hydration, resolvedNow, canViewPayments]
  );
  const edits = useHubRowEdits(builtRows, values.status);
  const { rows } = edits;

  // Rows that arrive by subscription skip the query's filter, so they are checked on their tags.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesPatientFilterTags(row.patient, { ...values, status: QUEUE_FILTER_ANY }) &&
          matchesToSchedule(row.patient) &&
          (values.status === QUEUE_FILTER_ANY || row.activeLead.status === values.status)
      ),
    [rows, values]
  );
  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  const { onNoteSaved, changeStatus, undoStatus } = edits;
  const columns = useMemo(
    () => [
      patientColumn<HubToScheduleRow>(),
      paymentTrackColumn<HubToScheduleRow>(),
      pnColumn<HubToScheduleRow>(),
      readySinceColumn<HubToScheduleRow>(),
      preferredDateColumn<HubToScheduleRow>(),
      lastContactColumn<HubToScheduleRow>(),
      notesColumn<HubToScheduleRow>({ onSaved: onNoteSaved }),
      statusColumn<HubToScheduleRow>({ onChange: changeStatus, onUndo: undoStatus, header: 'Lead status' }),
    ],
    [onNoteSaved, changeStatus, undoStatus]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={null}
        filters={[
          { label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') },
          { label: 'Payment track', value: values.track, options: TRACK_FILTER_OPTIONS, onChange: setFilter('track') },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          { label: 'Status', value: values.status, options: LEAD_STATUS_FILTER_OPTIONS, onChange: setFilter('status') },
        ]}
        isFiltered={isQueueFiltered(values, defaults)}
        onReset={() => setValues(defaults)}
      />
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading}
        initialSorting={[{ id: 'readySince', desc: false }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        getRowClassName={edits.rowClassName}
      />
      <QueueFooter
        shown={visibleRows.length}
        total={total}
        page={page}
        pageCount={Math.ceil((total ?? 0) / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
